import React from 'react';
import { Box, Button, Chip, Divider, Paper, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import { PERM_HINTS, statusColor } from '../constants';
import { fmtDateTime } from '../format';
import { DocumentCard, DocumentsAccess, PortalAction } from '../types';
import { BORDER, Caption, DARK, SOFT, SUCCESS, SideField, SideTitle, WARNING } from './cardKit';
import PortalActionsBlock from './PortalActionsBlock';

interface Props {
  doc: DocumentCard;
  access: DocumentsAccess;
  /** Есть только при праве «Просмотр действий в портале» */
  portalActions?: PortalAction[];
  /** Кнопка «Обработать» (документ в ошибке и есть право) */
  retryVisible: boolean;
  onRetry: () => void;
}

/** Левая панель карточки: реквизиты, короба, маршрут, история статусов, действия в портале */
const CardSidebar: React.FC<Props> = ({ doc, access, portalActions, retryVisible, onRetry }) => {
  const showPortal = Boolean(access.viewAudit && portalActions);
  return (
    <Box sx={{ width: { xs: '100%', md: 380 }, borderRight: { md: `1px solid ${BORDER}` }, bgcolor: '#fff', overflow: { xs: 'visible', md: 'auto' }, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Caption>Отправитель</Caption>
              <Typography variant="body2" sx={{ fontWeight: 600, color: DARK, lineHeight: 1.2 }}>{doc.sender || '-'}</Typography>
            </Box>
            <Box>
              <Caption>Получатель</Caption>
              <Typography variant="body2" sx={{ fontWeight: 600, color: DARK, lineHeight: 1.2 }}>{doc.recipient || '-'}</Typography>
            </Box>
          </Box>
          {doc.route === 'via' && (
            <Typography variant="caption" color="text.secondary">
              Через {doc.via || 'РЦ'}{doc.currentPoint ? ` · сейчас: ${doc.currentPoint}` : ''}
            </Typography>
          )}

          {doc.processStatus && (
            <>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <SideField label="Статус 1С" value={doc.processStatus} />
            </>
          )}
          <Divider sx={{ borderStyle: 'dashed' }} />

          <Box>
            <Caption>Создан / Обновлен</Caption>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{fmtDateTime(doc.createdAt)}</Typography>
            <Typography variant="caption" color="text.secondary">{fmtDateTime(doc.updatedAt)}</Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <SideField label="Автор" value={doc.author} />
            <SideField label="Пользователь 1С" value={doc.user1C} />
          </Box>
          <SideField
            label="Входящий"
            value={doc.incomingNumber ? `${doc.incomingNumber}${doc.incomingDate ? ` от ${fmtDateTime(doc.incomingDate)}` : ''}` : null}
            copyText={doc.incomingNumber}
          />
          <SideField label="Проверка 1С" value={doc.checkDate1c ? fmtDateTime(doc.checkDate1c) : null} />
          {doc.isHidden && <Box><Chip size="small" label="Скрыт у аптек" variant="outlined" sx={{ borderRadius: 1, borderColor: BORDER }} /></Box>}
        </Box>

        {doc.comment && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(WARNING, 0.05), borderColor: alpha(WARNING, 0.2) }}>
            <Typography variant="caption" gutterBottom sx={{ color: WARNING, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberRoundedIcon fontSize="inherit" /> КОММЕНТАРИЙ
            </Typography>
            <Typography variant="body2" sx={{ color: DARK }}>{doc.comment}</Typography>
          </Paper>
        )}
      </Box>

      {access.viewHistory && (
        <>
          <Divider />
          <Box sx={{ p: 3 }}>
            <SideTitle icon={<Inventory2OutlinedIcon fontSize="small" />}>Коробы ({doc.boxes.length || doc.boxCount})</SideTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {doc.boxes.length > 0 ? doc.boxes.map(b => (
                <Tooltip
                  key={b.id} arrow
                  title={<Box><div>ШК: {b.barcode || '-'}</div><div>Дата: {fmtDateTime(b.recordDate)}</div>{b.currentPoint && <div>Сейчас: {b.currentPoint}</div>}</Box>}
                >
                  <Chip
                    label={`№${b.boxNum}${b.barcode ? ` • ${b.barcode.slice(-4)}` : ''}`} size="small" variant="outlined"
                    sx={{ borderRadius: 1, bgcolor: SOFT, borderColor: BORDER, fontSize: '0.75rem', height: 24 }}
                  />
                </Tooltip>
              )) : (
                <Typography variant="caption" color="text.secondary">Нет коробов</Typography>
              )}
            </Box>
          </Box>
        </>
      )}

      {doc.routePoints.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 3 }}>
            <SideTitle icon={<AltRouteRoundedIcon fontSize="small" />}>Маршрут</SideTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {doc.routePoints.map(p => (
                <Box key={p.id} sx={{ pl: 1.5, borderLeft: `2px solid ${p.dateOut ? SUCCESS : p.dateIn ? '#3b82f6' : BORDER}` }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: DARK }}>{p.pointName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.dateIn ? `вход ${fmtDateTime(p.dateIn)}` : 'ожидается'}{p.dateOut ? ` · выход ${fmtDateTime(p.dateOut)}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}

      {(access.viewHistory || retryVisible) && (
        <>
          <Divider />
          <Box sx={{ p: 3, flexGrow: showPortal ? 0 : 1 }}>
            <SideTitle
              icon={<HistoryRoundedIcon fontSize="small" />}
              action={retryVisible ? (
                <Tooltip title={doc.canRetry ? '' : PERM_HINTS.retryOutOfZone}>
                  <span>
                    <Button
                      variant="contained" size="small" disableElevation color="warning" disabled={!doc.canRetry}
                      startIcon={<ReplayRoundedIcon fontSize="small" />} onClick={onRetry}
                      sx={{ fontWeight: 700, fontSize: '0.7rem', px: 1.5, py: 0.5, borderRadius: 1.5, textTransform: 'none' }}
                    >
                      Обработать
                    </Button>
                  </span>
                </Tooltip>
              ) : undefined}
            >
              {access.viewHistory ? 'История статусов' : 'Обработка документа'}
            </SideTitle>
            {access.viewHistory && doc.statusHistory.length === 0 && (
              <Typography variant="caption" color="text.secondary">История пуста</Typography>
            )}
            {access.viewHistory && (
              <Box sx={{ position: 'relative', pl: 1 }}>
                <Box sx={{ position: 'absolute', top: 4, bottom: 4, left: 12, width: 2, bgcolor: SOFT }} />
                {doc.statusHistory.map((h, idx) => (
                  <Box key={`${h.statusZid}-${idx}`} sx={{ display: 'flex', gap: 2, mb: 2.5, position: 'relative' }}>
                    <Box sx={{
                      width: 10, height: 10, borderRadius: '50%', mt: 0.5, flexShrink: 0, zIndex: 1,
                      bgcolor: idx === 0 ? statusColor(h.statusZid) : BORDER,
                      boxShadow: idx === 0 ? `0 0 0 2px ${alpha(statusColor(h.statusZid), 0.3)}` : 'none'
                    }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: DARK, lineHeight: 1.2 }}>{h.statusName}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {fmtDateTime(h.recordDate)}{h.username ? ` • ${h.username}` : ''}
                      </Typography>
                      {h.description && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic', bgcolor: SOFT, p: 0.5, borderRadius: 1 }}>
                          {h.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}

      {showPortal && portalActions && (
        <>
          <Divider />
          <Box sx={{ p: 3, flexGrow: 1 }}>
            <PortalActionsBlock actions={portalActions} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default React.memo(CardSidebar);
